// Port of `bin/crd-controller/src/helpers/name.rs` from lightmare.

const HASH_LEN = 52
const DELIMITER_LEN = 1
export const MAX_TOTAL_LEN = 63
export const MAX_PREFIX_LEN = MAX_TOTAL_LEN - HASH_LEN - DELIMITER_LEN // 10

const K8S_LABEL_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/

export function validatePrefix(prefix: string): void {
  if (prefix.length === 0) {
    throw new Error('Prefix must not be empty')
  }
  if (prefix.length > MAX_PREFIX_LEN) {
    throw new Error(`Prefix too long: max is ${MAX_PREFIX_LEN} chars`)
  }
  if (!K8S_LABEL_RE.test(prefix)) {
    throw new Error(
      'Prefix must be lowercase alphanumeric and hyphens only, no leading/trailing hyphens',
    )
  }
}

const BASE32_HEX_LOWER = '0123456789abcdefghijklmnopqrstuv'

function encodeBase32HexLowerNoPadding(bytes: Uint8Array): string {
  let out = ''
  let buffer = 0
  let bits = 0
  for (const byte of bytes) {
    buffer = (buffer << 8) | byte
    bits += 8
    while (bits >= 5) {
      bits -= 5
      out += BASE32_HEX_LOWER[(buffer >> bits) & 0x1f]
    }
  }
  if (bits > 0) {
    out += BASE32_HEX_LOWER[(buffer << (5 - bits)) & 0x1f]
  }
  return out
}

async function sha256(data: Uint8Array): Promise<Uint8Array> {
  const digest = await crypto.subtle.digest('SHA-256', data)
  return new Uint8Array(digest)
}

// Length-prefix each value so ["ab", "c"] and ["a", "bc"] hash differently.
function buildHashInput(values: readonly string[]): Uint8Array {
  const encoder = new TextEncoder()
  const encoded = values.map((v) => encoder.encode(v))
  const totalLen = encoded.reduce((acc, bytes) => acc + 8 + bytes.length, 0)
  const buffer = new Uint8Array(totalLen)
  const view = new DataView(buffer.buffer)
  let offset = 0
  for (const bytes of encoded) {
    view.setBigUint64(offset, BigInt(bytes.length), true)
    offset += 8
    buffer.set(bytes, offset)
    offset += bytes.length
  }
  return buffer
}

export async function safeName(
  prefix: string,
  values: readonly string[],
): Promise<string> {
  validatePrefix(prefix)
  const hashBytes = await sha256(buildHashInput(values))
  return `${prefix}-${encodeBase32HexLowerNoPadding(hashBytes)}`
}
